import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { shuffle } from 'lodash'; // Importing the shuffle function from a library like Lodash
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPromptQuizQuestion } from '../../api/DialogPromptApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';


interface DialogPromptQuizQuestionComponentProps {
    dialogPrompt: DialogPromptQuizQuestion;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

type AnswerOption = {
    id: number;
    word?: string;
    word_pinyin?: string[];
    image_url?: string | null;
};

const AnswerCard: React.FC<{
    answerOption: AnswerOption;
    onSelect: (answerOption: AnswerOption, correct: boolean) => void;
    isCorrect: (id: number) => boolean;
    displayPinyin?: boolean;
    interactive?: boolean;
    onPreview?: () => void;
}> = ({ answerOption, onSelect, isCorrect, displayPinyin, interactive = true, onPreview }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            m: 1,
            gap: 1,
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.2s ease',
            '&:hover': interactive ? { transform: 'translateY(-2px)', zIndex: 10 } : undefined,
        }}
    >
        {!!answerOption.word && !answerOption.image_url && (
            <Button
                variant="contained"
                onClick={() => interactive && onSelect(answerOption, isCorrect(answerOption.id))}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography style={{ fontFamily: 'KaiTi', fontSize: '24px' }}>
                        {answerOption.word}
                    </Typography>
                    {displayPinyin === true && answerOption.word_pinyin && !!answerOption.word_pinyin.length && (
                        <Typography style={{ fontSize: '12px' }}>
                            {answerOption.word_pinyin.join(' ')}
                        </Typography>
                    )}
                </div>
            </Button>
        )}
        {!!answerOption.image_url && (
            <>
                <img
                    src={answerOption.image_url}
                    alt={answerOption.word || ''}
                    onClick={() => {
                        if (interactive) onPreview?.();
                    }}
                    style={{
                        maxHeight: 125,
                        maxWidth: 125,
                        height: 'auto',
                        width: 'auto',
                        objectFit: 'contain',
                        cursor: interactive ? 'pointer' : 'default',
                        borderRadius: '12px',
                        transition: 'transform 0.2s ease, filter 0.2s ease',
                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
                    }}
                    onMouseOver={(e) => {
                        if (!interactive) return;
                        (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.08)';
                        (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))';
                    }}
                    onMouseOut={(e) => {
                        if (!interactive) return;
                        (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
                        (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))';
                    }}
                />
                {interactive && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onSelect(answerOption, isCorrect(answerOption.id))}
                        sx={{
                            minWidth: 80,
                            textTransform: 'none',
                            borderRadius: '8px',
                            padding: '6px 16px',
                            lineHeight: 1.2,
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#0062FF',
                                color: '#fff',
                                borderColor: '#0062FF',
                            }
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {!!answerOption.word && (
                                <Typography style={{ fontFamily: 'KaiTi', fontSize: '24px' }}>
                                    {answerOption.word}
                                </Typography>
                            )}
                            {displayPinyin === true && answerOption.word_pinyin && !!answerOption.word_pinyin.length && (
                                <Typography style={{ fontSize: '12px' }}>
                                    {answerOption.word_pinyin.join(' ')}
                                </Typography>
                            )}
                        </div>
                    </Button>
                )}
                {!interactive && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {!!answerOption.word && (
                            <Typography style={{ fontFamily: 'KaiTi', fontSize: '24px' }}>
                                {answerOption.word}
                            </Typography>
                        )}
                        {displayPinyin === true && answerOption.word_pinyin && !!answerOption.word_pinyin.length && (
                            <Typography style={{ fontSize: '12px' }}>
                                {answerOption.word_pinyin.join(' ')}
                            </Typography>
                        )}
                    </Box>
                )}
            </>
        )}
    </Box>
);

const DialogPromptQuizQuestionComponent: React.FC<DialogPromptQuizQuestionComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, isSettings = false }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [componentList, setComponentList] = React.useState<any[]>([]);
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleCorrectDone, setChatBubbleCorrectDone] = React.useState(false);


    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherChinesePromptAudio, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);

    const [shuffledAnswerOptionIdList, setShuffledAnswerOptionIdList] = React.useState<number[]>([]);
    const [supplementalPromptRendered, setSupplementalPromptRendered] = React.useState(false);

    // Build options from dialogPrompt (correct + incorrect)
    const answerOptionList: AnswerOption[] = React.useMemo(() => {
        const word_list = [
            ...dialogPrompt.incorrect_vocabulary_word_list,
            dialogPrompt.correct_vocabulary_word,
        ];
        return word_list.map(word => ({
            id: word.id,
            word: word.word,
            word_pinyin: word.word_pinyin,
            image_url: word.image_url ? word.image_url : null,
        }));
    }, [dialogPrompt]);


    const answerOptionByIdMap = React.useMemo(() => {
        const map = new Map<number, AnswerOption>();
        answerOptionList.forEach(answerOption => map.set(answerOption.id, answerOption));
        return map;
    }, [answerOptionList]);

    React.useEffect(() => {
        const answerOptionIdList = answerOptionList.map(o => o.id);
        setShuffledAnswerOptionIdList(shuffle(answerOptionIdList));
    }, [answerOptionList])

    // preview dialog state (for image answer options)
    const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);
    const previewAnswerOption = previewIndex !== null
        ? answerOptionByIdMap.get(shuffledAnswerOptionIdList[previewIndex]) ?? null
        : null;

    const handlePrev = () => {
        setPreviewIndex(prev => {
            if (prev === null) return prev;
            return prev === 0 ? shuffledAnswerOptionIdList.length - 1 : prev - 1;
        });
    };

    const handleNext = () => {
        setPreviewIndex(prev => {
            if (prev === null) return prev;
            return prev === shuffledAnswerOptionIdList.length - 1 ? 0 : prev + 1;
        });
    };

    const touchStartX = React.useRef<number | null>(null);
    const touchEndX = React.useRef<number | null>(null);
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.targetTouches[0].clientX; };
    const onTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.targetTouches[0].clientX; };
    const onTouchEnd = () => {
        if (touchStartX.current === null || touchEndX.current === null) return;
        const distance = touchStartX.current - touchEndX.current;
        if (distance > minSwipeDistance) handleNext();
        if (distance < -minSwipeDistance) handlePrev();
        touchStartX.current = null;
        touchEndX.current = null;
    };

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio, isSettings)
    }, [clearFooterList, dialogPrompt.prompt, dialogPrompt.teacher_chinese_prompt, dialogPrompt.yes_response, dialogPrompt.no_response, history, isSettings, setErrorMessage]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [componentList, chatBubbleContainerRef])

    const isCorrect = React.useCallback((id: number) => id === dialogPrompt.correct_vocabulary_word.id, [dialogPrompt.correct_vocabulary_word.id]);

    const handleAnswerClickRef = React.useRef<(answerOption: AnswerOption, correct: boolean) => void>(() => {});


    const handleAnswerClick = React.useCallback((answerOption: AnswerOption, correct: boolean) => {
        const additionalComponentList: React.ReactNode[] = []
        additionalComponentList.push(<AnswerCard answerOption={answerOption} onSelect={() => { }} isCorrect={isCorrect} displayPinyin={dialogPrompt.display_pinyin ?? undefined} interactive={false} key={`student-img-${answerOption.id}`} />)
        if (correct) {
            clearFooterList()
            const newChatBubble = (
                <ChatBubble
                    key={`yes-${answerOption.id}`}
                    isTeacher={true}
                    message={dialogPrompt.yes_response}
                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleCorrectDone}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, ...additionalComponentList, newChatBubble]);
            UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        } else {
            // reshuffle ids
            const newIds = shuffle(shuffledAnswerOptionIdList.slice());
            setShuffledAnswerOptionIdList(newIds);
            const newFooterList = newIds.map((id, index) => (
                <AnswerCard
                    key={id}
                    answerOption={answerOptionByIdMap.get(id)!}
                    onSelect={handleAnswerClickRef.current}
                    isCorrect={isCorrect}
                    displayPinyin={dialogPrompt.display_pinyin ?? undefined}
                    onPreview={() => setPreviewIndex(index)}
                />
            ));
            setActionList(newFooterList);

            const newChatBubble = (
                <ChatBubble
                    key={`no-${answerOption.id}`}
                    isTeacher={true}
                    message={dialogPrompt.no_response}
                    messageAudio={noResponseAudio ? noResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, ...additionalComponentList, newChatBubble]);
            UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        }
    }, [isCorrect, clearFooterList, dialogPrompt.display_pinyin, dialogPrompt.yes_response, dialogPrompt.no_response, dialogPrompt.id, yesResponseAudio, noResponseAudio, showAvatar, chatBubbleContainerRef, shuffledAnswerOptionIdList, answerOptionByIdMap, setActionList, setErrorMessage, history, location]);

    handleAnswerClickRef.current = handleAnswerClick;

    React.useEffect(() => {
        if (chatBubbleDone) {
            const newComponentList: React.ReactNode[] = [];
            if (!supplementalPromptRendered && (dialogPrompt.image_src || dialogPrompt.image_src_url)) {
                newComponentList.push(
                    <img
                        key="prompt-image"
                        src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                        alt={dialogPrompt.prompt}
                        style={{
                            maxHeight: '400px',
                            maxWidth: '400px',
                            height: 'auto',
                            width: 'auto',
                            margin: '0 auto',
                            display: 'block',
                        }}
                    />
                );
            }
            const tcpTrimmed = (dialogPrompt.teacher_chinese_prompt ?? '').trim();
            if (!supplementalPromptRendered && tcpTrimmed.length > 0 && tcpTrimmed !== '<Answer/>') {
                newComponentList.push(
                    <ChatBubble
                        key="teacher-chinese-prompt"
                        isTeacher={true}
                        message={dialogPrompt.teacher_chinese_prompt}
                        messageAudio={teacherChinesePromptAudio ? teacherChinesePromptAudio : null}
                        showAvatar={showAvatar}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                    />
                );
            }
            if (newComponentList.length > 0) {
                setComponentList((prevComponentList) => [...prevComponentList, ...newComponentList]);
            }
            if (!supplementalPromptRendered) {
                setSupplementalPromptRendered(true);
            }

            const shuffledAnswerList = shuffledAnswerOptionIdList.map((id, index) => {
                const answerOption = answerOptionByIdMap.get(id)!;
                return <AnswerCard key={id} answerOption={answerOption} onSelect={handleAnswerClickRef.current} isCorrect={isCorrect} displayPinyin={dialogPrompt.display_pinyin ?? undefined} onPreview={() => setPreviewIndex(index)} />;
            })

            setActionList(shuffledAnswerList);
        }
    }, [
        chatBubbleDone, shuffledAnswerOptionIdList, supplementalPromptRendered,
        dialogPrompt.image_src, dialogPrompt.image_src_url, dialogPrompt.prompt,
        dialogPrompt.teacher_chinese_prompt, dialogPrompt.display_pinyin,
        teacherChinesePromptAudio, showAvatar, chatBubbleContainerRef,
        answerOptionByIdMap, setActionList, handleAnswerClick, isCorrect,
    ]);

    React.useEffect(() => {
        if (chatBubbleCorrectDone) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleCorrectDone, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack spacing={2}>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {componentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}

            {/* Preview dialog for image answer options */}
            <Dialog
                open={previewIndex !== null}
                onClose={() => setPreviewIndex(null)}
                fullScreen
                PaperProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.96)', color: '#fff' }
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        height: '100vh',
                        overflow: 'hidden',
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                        }}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <IconButton
                            aria-label="Previous image"
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute', left: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                width: 48, height: 48,
                            }}
                        >
                            ‹
                        </IconButton>

                        {previewAnswerOption && (
                            <img
                                src={previewAnswerOption.image_url || 'broken'}
                                alt={previewAnswerOption.word || ''}
                                style={{
                                    maxWidth: '95vw', maxHeight: '85vh',
                                    width: 'auto', height: 'auto',
                                    objectFit: 'contain',
                                    borderRadius: '14px',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
                                    display: 'block',
                                }}
                            />
                        )}

                        <IconButton
                            aria-label="Next image"
                            onClick={handleNext}
                            sx={{
                                position: 'absolute', right: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                width: 48, height: 48,
                            }}
                        >
                            ›
                        </IconButton>

                        {previewIndex !== null && (
                            <Box
                                sx={{
                                    position: 'absolute', bottom: 16,
                                    left: '50%', transform: 'translateX(-50%)',
                                    px: 2, py: 0.5,
                                    borderRadius: '999px',
                                    backgroundColor: 'rgba(0,0,0,0.45)',
                                    color: '#ddd', fontSize: '13px',
                                    backdropFilter: 'blur(8px)',
                                }}
                            >
                                {previewIndex + 1} / {shuffledAnswerOptionIdList.length}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setPreviewIndex(null)} sx={{ color: '#ccc' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (previewAnswerOption) {
                                handleAnswerClick(previewAnswerOption, isCorrect(previewAnswerOption.id));
                                setPreviewIndex(null);
                            }
                        }}
                        className="secondaryButton"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DialogPromptQuizQuestionComponent;
